import type { CampaignState, ResumeBlocker, ReviewStatus } from "../../api/types";
import { StatusPill, type Tone } from "../../components/ui";

// UX-DESIGN.md §2.4's state banner vocabulary for the states
// campaign_detail emits (0052_campaign_portal.up.sql:1141-1189). The
// server owns the derivation; the portal owns the words. Every state that
// applies is shown — never just one picked by precedence.

export const CAMPAIGN_STATE: Record<CampaignState, { tone: Tone; label: string; detail: string }> = {
  draft: { tone: "neutral", label: "Draft", detail: "Not submitted for review yet. Nothing serves." },
  in_review: {
    tone: "warning",
    label: "In review",
    detail: "The config and creative versions are reviewed independently. Nothing new serves until both are approved and promoted.",
  },
  rejected: {
    tone: "error",
    label: "Review rejected",
    detail: "The pending candidate was rejected. What's live (if anything) is unchanged. See the reason on the version below.",
  },
  approved_ready_to_activate: {
    tone: "success",
    label: "Approved — ready to activate",
    detail: "Both versions passed review. Promote the release to make them live; approval and going live are separate steps.",
  },
  approved_scheduled: {
    tone: "info",
    label: "Approved — scheduled",
    detail: "The release is approved, but the schedule hasn't started yet.",
  },
  live: { tone: "success", label: "Live", detail: "Eligible to serve under the live release and schedule." },
  paused: { tone: "warning", label: "Paused", detail: "No new ad decisions. Resume re-checks eligibility first." },
  ended: { tone: "neutral", label: "Ended", detail: "Ending is permanent. Create a new campaign to run these ads again." },
  budget_exhausted: {
    tone: "warning",
    label: "Budget exhausted",
    detail: "Still live, but no further reservations are possible until the budget is increased (or tomorrow, for a daily budget).",
  },
  safety_suspended: {
    tone: "error",
    label: "Safety-suspended",
    detail: "Serving is stopped by a safety review. This isn't an ordinary pause, and it can't be resolved from here; platform review lifts it.",
  },
  suspended: {
    tone: "warning",
    label: "Suspended",
    detail: "Serving is suspended by the platform. Contact support for the reason and what's needed to lift it.",
  },
  payment_frozen: {
    tone: "error",
    label: "Payment-frozen",
    detail: "Your account's spending is frozen, so this campaign can't make new reservations. See the banner at the top of the page.",
  },
};

export function CampaignStatePills({ states }: { states: CampaignState[] }) {
  if (states.length === 0) return <StatusPill tone="neutral">No state</StatusPill>;
  return (
    <span className="row" style={{ gap: "var(--space-2)" }}>
      {states.map((s) => {
        const c = CAMPAIGN_STATE[s] ?? { tone: "neutral" as Tone, label: s };
        return (
          <StatusPill key={s} tone={c.tone}>
            {c.label}
          </StatusPill>
        );
      })}
    </span>
  );
}

/** Resume refusals — _campaign_resume_blocker (0052:645-687), in §2.4's "Resume blocked — …" form. */
export const RESUME_BLOCKED: Record<ResumeBlocker, string> = {
  campaign_ended: "Resume blocked — this campaign has ended, and ending is permanent.",
  no_live_release: "Resume blocked — there's no approved live release to resume. Promote an approved release first.",
  safety_suspended: "Resume blocked — a safety suspension is in effect. It needs platform review to lift.",
  campaign_suspended: "Resume blocked — the campaign is suspended by the platform. Contact support.",
  creative_not_approved: "Resume blocked — the live creative is no longer approved. Submit a revised creative for review.",
  destination_rescan_flagged: "Resume blocked — destination re-scan flagged an issue since this was paused. It has to pass review again.",
  destination_unverified: "Resume blocked — the destination domain's DNS proof is missing. Verify the domain again, then resume.",
};

export function resumeBlockedMessage(reason: string | undefined): string {
  return (reason && (RESUME_BLOCKED as Record<string, string>)[reason]) || "Resume blocked — the campaign isn't eligible to serve right now.";
}

export const REVIEW_STATUS: Record<ReviewStatus, { tone: Tone; label: string }> = {
  draft: { tone: "neutral", label: "Pending review" },
  in_review: { tone: "warning", label: "In review" },
  approved: { tone: "success", label: "Approved" },
  rejected: { tone: "error", label: "Rejected" },
};

export function ReviewPill({ status }: { status: ReviewStatus }) {
  const s = REVIEW_STATUS[status] ?? { tone: "neutral" as Tone, label: status };
  return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
}

/** "0.200000" → "20%" exactly: rates are 6-place decimals (platform_commercial_terms). */
export function formatRate(rate: string): string {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(rate.trim());
  if (!m) return rate;
  const frac = (m[2] ?? "").padEnd(2, "0");
  const whole = (m[1] ?? "0") + frac.slice(0, 2);
  const rest = frac.slice(2).replace(/0+$/, "");
  const pct = whole.replace(/^0+(?=\d)/, "");
  return `${pct}${rest ? "." + rest : ""}%`;
}
